import Disabled from '../../../../components/form/elements/disabled';
import Select, {
  emptySelectOption,
  Option,
} from '../../../../components/form/elements/select';
import DealType from '../../../../enums/deal-type';
import Date from '../../../../components/form/elements/date';
import { map, tap } from 'rxjs/operators';
import Paginate from '../../../../interfaces/paginate';
import Partner from '../../../../interfaces/partner';
import { PartnerService } from '../../../../services/partner.service';
import Watch, { WatchType } from '../../../../components/form/elements/watch';
import asyncCategories from './async-categories';
import asyncTypes from './async-types';
import asyncProducts from './async-products';
import Switch from '../../../../components/form/elements/switch';
import ClassifierValueType from '../../../../enums/classifier-value-type';
import { ClassifierService } from '../../../../services/classifier.service';
import { DealService } from '../../../../services/deal.service';
import { ProductService } from '../../../../services/product.service';
import { TFunction } from 'i18next';
import Amount from '../../../../components/form/elements/amount';
import Decimal from '../../../../components/form/elements/decimal';
import Show from '../../../../components/form/elements/show';
import ClassifierGroup from '../../../../enums/classifier-group';
import Classifier from '../../../../enums/classifier';
import ClassifierValue from '../../../../interfaces/classifierValue';
import Farm from '../../../../interfaces/farm';
import Warehouse from '../../../../interfaces/warehouse';
import { WarehouseService } from '../../../../services/warehouse.service';
import TextArea from '../../../../components/form/elements/text-area';
import Deal from '../../../../interfaces/deal';
import Text from '../../../../components/form/elements/text';
import { EmployeeService } from '../../../../services/employee.service';
import Employee from '../../../../interfaces/employee';
import toFixed from 'accounting-js/lib/toFixed.js';
import { DebouncedWarning } from '../../../../components/form/elements/warning';
import { RemainderService } from '../../../../services/remainder.service';
import { Observable } from 'rxjs';
import RecycleDeal from '../../../../interfaces/recycle-deal';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import CreateDealTable from './purchased/purchased-table';
import CreateDealUsedTable from './recycled/recycled-used-table';
import CreateDealGainedTable from './recycled/recycled-gained-table';
import MakeButton from '../../../../components/button/make-button';
import { MouseEventHandler } from 'react';
import { handleAmount } from './use-deal-form-calculations';
import WarehousePartyNumber from './warehouse-party-number';
import LostRadioGroup from './lost/lost-radio-group';
import { DealProductWarehouse } from '../../../../interfaces/deal-product';
import { CalendarIcon } from '../../../../assets/icons';
import { setDealProductPartyNumbers } from './use-deal-party-numbers';

const OUTGOING_DEALS = [
  DealType.sold,
  DealType.self_consumed,
  DealType.recycled,
  DealType.spent,
  DealType.lost,
  DealType.moved,
];

const newPartyNumberDealTypeList = [
  DealType.sold,
  DealType.spent,
  DealType.self_consumed,
  DealType.moved,
];

export function submitFormMap(form: Deal | RecycleDeal) {
  if ((form.type as Option)?.value === 'recycled') {
    const recycleForm = form as RecycleDeal;
    return {
      ...recycleForm,
      type: (recycleForm.type as Option)?.value,
      lost_products: recycleForm.lost_products
        .filter((v) => v?.product_id)
        .map((v) => ({
          id: v.id,
          product_category_id: (v.product_category_id as Option)?.value,
          product_type_id: (v.product_type_id as Option)?.value,
          product_id: (v.product_id as Option)?.value,
          amount: v.amount,
          product_obtain_type_id: v?.product_obtain_type_id,
          unit: (v.unit as Option)?.value,
          product_warehouses: v?.product_warehouses?.map(
            (pw) =>
              ({
                id: pw?.id,
                party_number: (pw.party_number as Option)?.value,
                warehouse_id: (pw.warehouse_id as Option)?.value,
                amount: pw.amount,
              } as unknown as DealProductWarehouse)
          ),
        })),
      gained_products: recycleForm.gained_products
        .filter((v) => v?.product_id)
        .map((v) => ({
          id: v.id,
          product_category_id: (v.product_category_id as Option)?.value,
          product_type_id: (v.product_type_id as Option)?.value,
          product_obtain_type_id: v?.product_obtain_type_id,
          product_id: (v.product_id as Option)?.value,
          amount: v.amount,
          unit: (v.unit as Option)?.value,
          price: v.price,
          sum: v.sum,
          vat_rate_id: (v.vat_rate_id as Option)?.value,
          product_warehouses: v?.product_warehouses?.map(
            (pw) =>
              ({
                id: pw?.id,
                party_number: (pw.party_number as Option)?.value,
                warehouse_id: (pw.warehouse_id as Option)?.value,
                amount: pw.amount,
              } as unknown as DealProductWarehouse)
          ),
        })),
    } as RecycleDeal;
  } else {
    const dealForm = form as Deal;
    return {
      ...dealForm,
      type: (form.type as Option)?.value,
      products: dealForm.products?.map((v) => ({
        id: v?.id,
        product_category_id: (v.product_category_id as Option)?.value,
        product_type_id: (v.product_type_id as Option)?.value,
        product_obtain_type_id: v?.product_obtain_type_id
          ? v?.product_obtain_type_id
          : null,
        obtain_type_id: (form.type as Option)?.value,
        product_id: (v.product_id as Option)?.value,
        packaging_type_id: v?.packaging_type_id?.value,
        packaging_size: v?.packaging_size,
        packaging_unit: v?.packaging_unit?.value,
        packaging_amount: v?.packaging_amount,
        expires_at: v?.expires_at,
        amount: v.amount,
        unit: (v.unit as Option)?.value,
        price: v.price,
        sum: v.sum,
        reason: v?.reason,
        vat_rate_id: (v.vat_rate as Option)?.value,
        to_warehouse_id: (v.to_warehouse_id as Option)?.value,
        product_warehouses: v?.product_warehouses?.map((pw) => ({
          id: pw?.id,
          party_number: (pw.party_number as Option)?.value,
          warehouse_id: (pw.warehouse_id as Option)?.value,
          amount: pw.amount,
        })),
      })),
    } as any;
  }
}

export function dealType(t: TFunction, horizontal: boolean = false) {
  return Disabled(
    Select(
      'type',
      {
        options: [
          {
            label: t(`classifiers.${DealType.produced}`),
            value: DealType.produced,
          },
          {
            label: t(`classifiers.${DealType.purchased}`),
            value: DealType.purchased,
          },
          {
            label: t(`classifiers.${DealType.sold}`),
            value: DealType.sold,
          },
          {
            label: t(`classifiers.${DealType.spent}`),
            value: DealType.spent,
          },
          {
            label:
              t(`classifiers.${DealType.lost}`) +
              '/' +
              t(`classifiers.${DealType.leftover}`).toLowerCase(),
            value: DealType.lost,
          },
          {
            label: t(`classifiers.${DealType.recycled}`),
            value: DealType.recycled,
          },
          {
            label: t(`classifiers.${DealType.self_consumed}`),
            value: DealType.self_consumed,
          },
          {
            label: t(`classifiers.${DealType.moved}`),
            value: DealType.moved,
          },
        ],
        label: 'pages.warehouse.deals.type',
      },
      horizontal
    )
  );
}

export function date(horizontal = false, isCalendarIcon = false) {
  return Date(
    'date',
    'pages.warehouse.deals.date',
    null,
    null,
    horizontal,
    CalendarIcon
  );
}

export function partner(
  partnerService: PartnerService,
  farmId: string,
  horizontal: boolean = false
) {
  return Select(
    'partner_id',
    {
      asyncOptions: (value, loadedOptions, { page }) =>
        partnerService.index(farmId, { page, name: value }).pipe(
          map((response: Paginate<Partner>) => ({
            hasMore: response.total === response.per_page,
            options: response.data.map((v) => ({
              label: v.name,
              value: v.id,
            })),
            additional: { page: response.current_page + 1 },
          }))
        ),
      label: 'pages.warehouse.deals.partner_purchased',
    },
    horizontal
  );
}

export function product(
  t: TFunction,
  dealService: DealService,
  productService: ProductService,
  farmId: string,
  dealType: DealType,
  creatable: boolean = false,
  obtainedProducts: boolean = true,
  horizontal: boolean = false,
  extraLabel: string = '',
  handleSpecialProducts?: any,
  dealSubType: DealType = null,
  originalValues = null,
  keyValue = null
) {
  return [
    Watch(
      Select(
        'product_category_id',
        {
          asyncOptions: asyncCategories({
            t,
            dealService,
            productService,
            farmId,
            classifierKeys: obtainedProducts
              ? [Classifier.OBTAINED_PRODUCT_CATEGORIES_AND_TYPES]
              : [Classifier.PRODUCT_CATEGORIES_AND_TYPES],
          }),
          grouped: true,
          label: `pages.warehouse.deals.product_category${extraLabel}`,
          cacheOn: ['product_obtain_type_id'],
        },
        horizontal
      ),
      ({ value, setValue, getValues, isTouched }) => {
        if (!value?.value || !isTouched) {
          return;
        }

        if (handleSpecialProducts) {
          handleSpecialProducts(value, 'category');
        }

        const [productType, product] = getValues([
          'product_type_id',
          'product_id',
        ]);

        if (productType?.parent?.value !== value.value) {
          setValue('product_type_id', emptySelectOption, {
            shouldValidate: true,
          });
        }

        if (product?.category?.value !== value.value) {
          setValue('product_id', emptySelectOption, {
            shouldValidate: true,
          });

          if (
            newPartyNumberDealTypeList.includes(dealType) ||
            (dealType === DealType.lost && dealSubType === DealType.lost) ||
            (dealType === DealType.recycled && keyValue === null)
          ) {
            setValue('product_warehouses', []);
          }
        }

        setValue('packaging_unit', emptySelectOption);
        setValue('unit', emptySelectOption);
        setValue('price', '');
      }
    ),
    Watch(
      Select(
        'product_type_id',
        {
          asyncOptions: asyncTypes({
            t,
            productService,
            dealService,
            farmId,
            classifierKeys: obtainedProducts
              ? [Classifier.OBTAINED_PRODUCT_TYPES]
              : [Classifier.PRODUCT_TYPES],
            dealType,
            handleSpecialProducts,
          }),
          grouped: true,
          label: `pages.warehouse.deals.product_type${extraLabel}`,
          cacheOn: ['product_obtain_type_id', 'product_category_id.value'],
        },
        horizontal
      ),
      ({ value, setValue, getValues, isTouched }) => {
        if (!value?.value || !isTouched) {
          return;
        }

        if (handleSpecialProducts) {
          handleSpecialProducts(value, 'type');
        }

        if (getValues('product_category_id')?.value !== value?.parent?.value) {
          setValue('product_category_id', value.parent, {
            shouldValidate: true,
          });
        }

        const [product] = getValues(['product_id']);

        if (product?.type?.value !== value.value) {
          setValue('product_id', emptySelectOption, {
            shouldValidate: true,
          });
          if (
            newPartyNumberDealTypeList.includes(dealType) ||
            (dealType === DealType.lost && dealSubType === DealType.lost) ||
            (dealType === DealType.recycled && keyValue === null)
          ) {
            setValue('product_warehouses', []);
          }
        }

        setValue('packaging_unit', emptySelectOption);
        setValue('unit', emptySelectOption);
        setValue('price', '');
      }
    ),
    (() => {
      const params = {
        asyncOptions: asyncProducts({
          productService,
          farmId,
          classifierKeys: obtainedProducts
            ? [
                Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                Classifier.OBTAINED_PRODUCT_CATEGORIES_AND_TYPES,
              ]
            : [Classifier.PRODUCT_CATEGORIES_AND_TYPES],
          t,
          dealType,
        }),
        label: `pages.warehouse.deals.name${extraLabel}`,
        cacheOn: [
          'product_obtain_type_id',
          'product_category_id.value',
          'product_type_id.value',
        ],
        grouped: true,
      };

      const watchCallback = ({
        setValue,
        value,
        getValues,
        isTouched,
      }: WatchType) => {
        if (!value?.value || !isTouched) {
          return;
        }

        if (
          value?.__isNew__ &&
          value?.type?.value &&
          value?.type?.value !== getValues('product_type_id')?.value
        ) {
          setValue('product_type_id', value?.type);
        }

        if (!value?.__isNew__) {
          setValue('price', value?.price ?? '');

          if (
            value?.category?.value !== getValues('product_category_id')?.value
          ) {
            setValue('product_category_id', value?.category, {
              shouldValidate: true,
            });
          }

          if (OUTGOING_DEALS.includes(dealType)) {
            setValue('product_obtain_type_id', value.obtain_type_id);
          }

          if (value?.type?.value !== getValues('product_type_id')?.value) {
            setValue('product_type_id', value?.type, {
              shouldValidate: true,
            });
          }

          if (handleSpecialProducts) {
            handleSpecialProducts(value, 'product');
          }

          if (
            newPartyNumberDealTypeList.includes(dealType) ||
            (dealType === DealType.lost && dealSubType === DealType.lost) ||
            (dealType === DealType.recycled && keyValue === null)
          ) {
            setValue('product_warehouses', []);
            setDealProductPartyNumbers(
              farmId,
              dealType,
              dealService,
              setValue,
              getValues,
              originalValues
            );
          }
        }

        if (value?.obtain_type_id) {
          setValue('product_obtain_type_id', value.obtain_type_id);
        }

        if (value?.unit) {
          setValue('packaging_unit', value?.unit);
        }
      };

      const basicField = Watch(
        Select('product_id', params, horizontal),
        watchCallback
      );

      if (creatable) {
        return Switch(
          basicField,
          Watch(
            Select('product_id', { ...params, creatable: true }, horizontal),
            watchCallback
          ),
          'product_category_id',
          (productCategory) =>
            [
              ClassifierValueType.PLANT_PROTECTION_PRODUCTS,
              ClassifierValueType.FERTILIZERS,
            ].includes(productCategory?.type)
        );
      } else {
        return basicField;
      }
    })(),
  ];
}

export function amount(
  horizontal: boolean = false,
  calculationValues?: any,
  triggerWarehouse: any = {}
) {
  return Watch(
    Amount(
      { amount: 'amount', unit: 'unit' },
      {
        asyncOptions: async (
          value,
          loadedOptions,
          { parentName, getValues }
        ) => {
          return {
            hasMore: false,
            options:
              getValues(
                parentName
                  ? parentName.concat('.product_type_id')
                  : 'product_type_id'
              )?.units?.map((v: string) => ({
                label: v,
                value: v,
              })) ?? [],
            additional: {},
          };
        },
        cacheOn: ['product_type_id.value'],
        label: 'pages.warehouse.deals.amount',
        onDisabled: 'both',
      },
      horizontal
    ),
    ({ value: [amount], parentName, getValues, setValue }) => {
      if (triggerWarehouse.enableNewPartyNumber) {
        setDealProductPartyNumbers(
          triggerWarehouse.farmId,
          triggerWarehouse.dealType,
          triggerWarehouse.dealService,
          setValue,
          getValues,
          triggerWarehouse.originalFormValues
        );
      }

      return handleAmount(
        'amount',
        amount,
        parentName,
        setValue,
        getValues,
        calculationValues
      );
    }
  );
}

export function price(classifierService: ClassifierService, farm: Farm) {
  return [
    Watch(
      Decimal('price', 'pages.warehouse.deals.price'),
      ({ value, parentName, setValue, getValues, touchedFields }) => {
        const amount = toFixed(
          parseFloat(
            getValues(parentName ? parentName.concat('.amount') : 'amount')
          ),
          3
        );

        if (amount && value) {
          setValue(
            parentName ? parentName.concat('.sum') : 'sum',
            toFixed(parseFloat(amount) * parseFloat(value), 2)
          );
        }

        const packagingAmount = getValues('packaging_amount');
        const packagingSize = getValues('packaging_size');

        if (packagingAmount && packagingSize && value) {
          const amount = toFixed(
            parseFloat(packagingAmount) * parseFloat(packagingSize),
            3
          );
          setValue('sum', toFixed(amount * parseFloat(value), 2));
        }
      }
    ),
    Watch(
      Decimal('sum', 'pages.warehouse.deals.sum'),
      ({ value, parentName, setValue, getValues }) => {
        const amount = getValues(
          parentName ? parentName.concat('.amount') : 'amount'
        );
        if (amount && value) {
          setValue(
            parentName ? parentName.concat('.price') : 'price',
            toFixed(parseFloat(value) / parseFloat(amount), 2)
          );
        }

        const vatRate = getValues(
          parentName ? parentName.concat('.vate_rate_id') : 'vat_rate_id'
        )?.label;

        if (vatRate && value) {
          setValue(
            parentName ? parentName.concat('.vat') : 'vat',
            toFixed(parseFloat(value) * (parseFloat(vatRate) / 100), 2)
          );
        }
      }
    ),
    Show(
      [
        Watch(
          Select('vat_rate_id', {
            asyncOptions: (value, loadedOptions, { page }) =>
              classifierService
                .values(ClassifierGroup.WAREHOUSE, Classifier.VAT_RATES, {
                  value,
                })
                .pipe(
                  map((response: ClassifierValue[]) => ({
                    hasMore: false,
                    options: response.map((v) => ({
                      label: v.value,
                      value: v.id,
                      rate: v.value,
                    })),
                    additional: {},
                  }))
                ),
            label: 'pages.warehouse.deals.vat_rate',
          }),
          ({ value, parentName, setValue, getValues }) => {
            value = Array.isArray(value) ? value[0] : value;
            const sum = getValues(
              parentName ? parentName.concat('.sum') : 'sum'
            );

            if (sum && value?.rate) {
              setValue(
                parentName ? parentName.concat('.vat') : 'vat',
                toFixed(parseFloat(sum) * (parseFloat(value.rate) / 100), 2)
              );
            }
          }
        ),
        Disabled(
          Watch(
            Decimal('vat', 'pages.warehouse.deals.vat'),
            ({ value, parentName, getValues, setValue }) => {
              value = Array.isArray(value) ? value[0] : value;
              const sum = getValues(
                parentName ? parentName.concat('.sum') : 'sum'
              );

              if (sum && value) {
                setValue(
                  parentName
                    ? parentName.concat('.sum_with_vat')
                    : 'sum_with_vat',
                  toFixed(parseFloat(sum) + parseFloat(value), 2)
                );
              }
            }
          )
        ),
        Disabled(Decimal('sum_with_vat', 'pages.warehouse.deals.sum_with_vat')),
      ],
      [],
      () => !!farm?.vat
    ),
  ];
}

export function warehouse(
  warehouseService: WarehouseService,
  farmId: string,
  filterWithProduct: boolean = false,
  horizontal?: boolean,
  name: string = 'warehouse_id',
  label: string = 'warehouse'
) {
  return Select(
    name,
    {
      asyncOptions: (
        value,
        loadedOptions,
        { page, parentName, getValues, setValue }
      ) => {
        return warehouseService
          .index(farmId, {
            page,
            name: value,
            product: filterWithProduct
              ? getValues(
                  parentName ? parentName.concat('.product_id') : 'product_id'
                ).value
              : null,
          })
          .pipe(
            tap((response: Paginate<Warehouse>) => {
              if (response.data.length === 1) {
                setValue(
                  parentName ? parentName.concat('.' + name) : name,
                  {
                    label: response.data[0].name,
                    value: response.data[0].id,
                  },
                  {
                    shouldValidate: true,
                  }
                );
              }
            }),
            map((response: Paginate<Warehouse>) => ({
              hasMore: response.total === response.per_page,
              options: response.data.map((v) => ({
                label: v.name,
                value: v.id,
              })),
              additional: { page: response.current_page + 1 },
            }))
          );
      },
      label: 'pages.warehouse.deals.' + label,
      cacheOn: filterWithProduct ? ['product_id.value'] : [],
    },
    horizontal
  );
}

export function partyNumber(fromType: boolean) {
  return Text(
    'product_warehouses.0.party_number.value',
    'pages.warehouse.deals.party_number',
    null,
    null,
    fromType
  );
}

export function comment() {
  return TextArea('comment', 'pages.warehouse.deals.comment');
}

export function employee(
  employeeService: EmployeeService,
  farmId: string,
  horizontal?: boolean
) {
  return Select(
    'employee_id',
    {
      asyncOptions: (value, loadedOptions, { page }) =>
        employeeService.index(farmId, { page, name: value }).pipe(
          map((response: Paginate<Employee>) => ({
            hasMore: response.total === response.per_page,
            options: response.data.map((v) => ({
              label: `${v.last_name} ${v.first_name}`,
              value: v.id,
            })),
            additional: { page: response.current_page + 1 },
          }))
        ),
      label: 'pages.warehouse.deals.employee',
    },
    horizontal
  );
}

export function remainderWarning(
  t: TFunction,
  remainderService: RemainderService,
  farmId: string
) {
  return DebouncedWarning(
    t('Norādītajam produktam noliktavā nav pietiekams atlikums'),
    [
      'date',
      'product_obtain_type_id',
      'product_category_id',
      'product_type_id',
      'product_id',
      'unit',
      'amount',
    ],
    ([
      date,
      productObtainTypeId,
      productCategoryId,
      productTypeId,
      productId,
      unit,
      amount,
    ]: any): Observable<boolean> => {
      return remainderService.validate(
        farmId,
        date,
        productId?.obtain_type_id ?? productObtainTypeId,
        productCategoryId?.value,
        productTypeId?.value,
        productId?.value,
        unit?.value,
        amount
      );
    }
  );
}

export function makeButton(
  label: string,
  className?: string,
  wrapperClass?: string,
  icon?: IconDefinition,
  iconAfterWord?: boolean,
  handleClick?: MouseEventHandler<HTMLButtonElement>,
  iconAfterImg?: string
) {
  return MakeButton(
    label,
    className,
    wrapperClass,
    icon,
    iconAfterWord,
    handleClick,
    iconAfterImg
  );
}

export const renderDealTable = (mode) => {
  return () => CreateDealTable(mode);
};

export const renderDealUsedTable = (mode) => {
  return () => CreateDealUsedTable(mode);
};

export const renderDealGainedTable = (mode) => {
  return () => CreateDealGainedTable(mode);
};

export const renderWarehousePartyNumber = () => {
  return () => WarehousePartyNumber();
};

export const renderLostRadioGroup = () => {
  return () => LostRadioGroup();
};
